import { Component, Prop, h } from '@stencil/core';

@Component({
  tag: 'a1-link',
  styleUrl: 'a1-link.scss',
  scoped: true,
})
export class A1Link {
  /**
   * Link size.
   */
  @Prop({ reflect: true }) size: 'm' | 's' = 'm';

  /**
   * Link variant.
   */
  @Prop({ reflect: true }) type: 'primary' | 'secondary' = 'primary';

  /**
   * Toggle for enabling / disabling link.
   */
  @Prop({ reflect: true }) disabled: boolean = false;

  /**
   * Toggle for regular / inverted link appearance.
   */
  @Prop({ reflect: true }) inverse: boolean = false;

  /**
   * Link icon position relative to link text.
   */
  @Prop({ reflect: true }) iconPosition: 'before' | 'after' | 'none' = 'none';

  /**
   * Icon name.
   */
  @Prop({ reflect: true }) iconName: string = 'download';

  /**
   * An alternate description to use for assistive devices. Label will be applied to link when link text is empty.
   */
  @Prop({ reflect: true }) label: string = 'Link Icon';

  /**
   * Text label for link.
   */
  @Prop({ reflect: true }) text: string = 'Link Text';

  /**
   * Link path.
   */
  @Prop({ reflect: true }) href: string = '#';

  /**
   * Path target.
   */
  @Prop({ reflect: true }) target: '_self' | '_blank' | '_parent' | '_top' = '_self';

  private handleClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
    }
  }

  private renderIcon(): JSX.Element | null {
    if (this.text === '' || this.iconPosition !== 'none') {
      return (
        <span class="link-icon">
          <a1-icon size={this.size} icon={this.iconName}></a1-icon>
        </span>
      );
    }
    return null;
  }

  render() {
    const finalPosition = 'icon-' + this.iconPosition;
    const classes = {
      [this.size]: true,
      [this.type]: true,
      'disabled': this.disabled,
      'inverse': this.inverse,
      [finalPosition]: this.iconPosition !== 'none',
      'icon-only': this.text === '',
    };

    return (
      <a
        href={this.disabled ? undefined : this.href}
        target={this.disabled ? undefined : this.target}
        class={classes}
        onClick={this.handleClick}
        tabindex={this.disabled ? undefined : '0'}
        aria-disabled={this.disabled ? 'true' : undefined}
        aria-label={this.text === '' ? this.label : undefined}
      >
        {this.renderIcon()}
        {this.text}
      </a>
    );
  }
}
